import { CheckIcon, CopyIcon, DownloadIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Recovery codes are shown exactly once. Each one signs in a single time when the phone is lost, so the
 * person is nudged to save them somewhere safe before continuing.
 */
export function RecoveryCodes({ codes, appName, onDone, doneLabel = 'I saved my codes', className }: { codes: string[]; appName: string; onDone?: () => void; doneLabel?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const text = `${appName} recovery codes\nEach code signs you in once if you lose access to your other verification methods. Keep them somewhere safe.\n\n${codes.join('\n')}\n`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const download = () => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${appName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-recovery-codes.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className={cn('grid gap-4', className)}>
      <div className="bg-muted/50 border p-3 text-sm leading-relaxed">
        <p className="font-medium">Save these recovery codes</p>
        <p className="text-muted-foreground mt-1">Each code works once if you lose your phone or cannot receive codes. They are shown only now.</p>
      </div>
      <ul className="grid grid-cols-2 gap-2 font-mono text-sm" aria-label="Recovery codes">
        {codes.map((c) => (
          <li key={c} className="bg-card border px-3 py-2 tracking-wide select-all">
            {c}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
          {copied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />} {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={download}>
          <DownloadIcon aria-hidden /> Download
        </Button>
        {onDone ? (
          <Button type="button" size="sm" className="ml-auto" onClick={onDone}>
            {doneLabel}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
